/**
 * planner.js — client for the Job Planner (contract v1.0, base path /planner).
 *
 * The planner sits IN FRONT OF the nesting/export flows: it turns demand
 * (order lines + due dates) into ranked imposition plans whose `up_counts`
 * (copies-per-SKU-per-sheet) feed the existing MNE/Export Vector flows. It
 * returns NUMBERS, NOT GEOMETRY — to draw the sheet you take a plan's counts
 * and call the MNE layout flow; this client never produces placed_vertices.
 *
 * Pure data/transport — no React, no DOM. Unit-testable in Node.
 *
 * Endpoints:
 *   POST /planner/skus              upsert SKUs (idempotent by sku_id)
 *   GET  /planner/skus              list SKUs
 *   POST /planner/order-lines       add order lines
 *   POST /planner/order-lines/csv   add order lines from a CSV (multipart)
 *   GET  /planner/order-lines       list order lines
 *   POST /planner/plan              request ranked plans  ← the main call
 *
 * Forward-compatibility (contract §7): treat responses as open maps (ignore
 * unknown fields), read `objective` / `require_signoff` from the response
 * rather than hard-coding, and model a candidate as potentially multi-sheet so
 * the v1.4 "set of forms" change is absorbed without a rewrite.
 */

import { parseErrorDetail } from './exportVector';
import { authFetch } from './auth';
// rev 11 §3.1 / §6.5: engine policy and `required_passthrough` are refused on
// SIX routes, four of them planner routes. One strip, one refused list — see
// prepress.js. ⑭h removed these from the export builders only.
import { stripEnginePolicy } from './prepress';

/** Thrown for any non-2xx /planner response. Carries HTTP status for branching. */
export class PlannerApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'PlannerApiError';
    this.status = status;     // 400 | 422 | 500 | 503 | ...
    this.body = body;
  }
}

async function readError(resp) {
  let body = null;
  try { body = await resp.json(); } catch { /* non-JSON */ }
  return new PlannerApiError(parseErrorDetail(body), resp.status, body);
}

async function getJson(apiBase, path, signal) {
  const resp = await authFetch(`${apiBase}${path}`, { signal });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

async function postJson(apiBase, path, body, signal) {
  const resp = await authFetch(`${apiBase}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/* ------------------------------------------------------------------ */
/* SKUs (§3.1)                                                          */
/* ------------------------------------------------------------------ */

/**
 * Upsert one or more SKUs (idempotent by sku_id). `skus` is an array of:
 *   { sku_id, name?, die_polygon: number[][], substrate?, inks?, coating? }
 * die_polygon: mm, Y-up, CCW, local origin (min x = min y = 0) — same contract as MNE.
 */
export function skuUpsertBody(skus) {
  if (!Array.isArray(skus)) throw new Error('upsertSkus: skus must be an array');
  // rev 11 §3.1 newly names this route. A SKU record is a DURABLE carton
  // template, so a refused key parked here would be re-sent on every plan that
  // referenced the SKU — a stored defect rather than a one-off 400.
  return stripEnginePolicy(skus);
}

export function upsertSkus({ apiBase, skus, signal }) {
  return postJson(apiBase, '/planner/skus', skuUpsertBody(skus), signal);
}

export function listSkus({ apiBase, signal }) {
  return getJson(apiBase, '/planner/skus', signal);
}

/* ------------------------------------------------------------------ */
/* Order lines (§3.2)                                                   */
/* ------------------------------------------------------------------ */

/**
 * Add order lines (the demand). `lines` is an array of:
 *   { sku_id, quantity, due_date?, priority?, moq?,
 *     over_tolerance_pct?, under_tolerance_pct?, line_id? }
 */
export function addOrderLines({ apiBase, lines, signal }) {
  if (!Array.isArray(lines)) throw new Error('addOrderLines: lines must be an array');
  return postJson(apiBase, '/planner/order-lines', lines, signal);
}

export function listOrderLines({ apiBase, signal }) {
  return getJson(apiBase, '/planner/order-lines', signal);
}

/**
 * Upload order lines from a CSV (spreadsheet export). Header row, case-insensitive,
 * column order free. Required columns: sku_id, quantity. Bad rows are skipped with
 * warnings rather than failing the whole upload.
 *
 * @returns {Promise<{ added: number, order_lines: Array, warnings: string[] }>}
 */
export async function uploadOrderLinesCsv({ apiBase, file, signal }) {
  const form = new FormData();
  form.append('file', file);     // single file part
  const resp = await authFetch(`${apiBase}/planner/order-lines/csv`, {
    method: 'POST', body: form, signal,
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/* ------------------------------------------------------------------ */
/* Plan (§3.3) — the main call                                          */
/* ------------------------------------------------------------------ */

/**
 * Request ranked plans. SKUs may be referenced by id (if registered) or supplied
 * inline via sku_defs.
 *
 * @param {Object} args
 * @param {Array}  [args.orderLines]  OrderLine[] — at most one line per SKU. Provide
 *                 orderLines OR lineIds (or both); neither → 422.
 * @param {Array}  [args.lineIds]   stored order-line ids to plan by reference (§3.3);
 *                 referenced lines bring their saved tolerances.
 * @param {Object} args.sheet       SheetSpec { width, height, kerf?, margin_* }.
 * @param {Array}  [args.skuDefs]   inline SKU definitions, merged over the registry.
 * @param {string} [args.objective] "min_overrun" | "min_sheets" | "max_utilization".
 * @param {number} [args.overTolerancePct]
 * @param {number} [args.underTolerancePct]
 * @param {number} [args.returnTopK]
 * @returns {Promise<PlanResponse>}  (see §3.3)
 * @throws {PlannerApiError}
 */
export function planRequestBody({
  orderLines, lineIds, sheet, skuDefs, objective,
  overTolerancePct, underTolerancePct, returnTopK,
}) {
  const body = { sheet };
  if (orderLines && orderLines.length) body.order_lines = orderLines;
  if (lineIds && lineIds.length) body.line_ids = lineIds;             // §3.3 plan-by-reference
  if (skuDefs && skuDefs.length) body.sku_defs = skuDefs;
  if (objective) body.objective = objective;
  if (overTolerancePct != null) body.over_tolerance_pct = overTolerancePct;
  if (underTolerancePct != null) body.under_tolerance_pct = underTolerancePct;
  if (returnTopK != null) body.return_top_k = returnTopK;
  // rev 11 §3.1, applied at the LAST point before transport and applied DEEP,
  // because these keys ride inside `sheet` and inside each sku_defs[] entry —
  // not only at the top level.
  return stripEnginePolicy(body);
}

export function requestPlan(args) {
  return postJson(args.apiBase, '/planner/plan', planRequestBody(args), args.signal);
}

/* ------------------------------------------------------------------ */
/* Planner config (brief §4) — shop-wide defaults                       */
/* ------------------------------------------------------------------ */

/** Read the current planner config (populate the Settings form). */
export function getConfig({ apiBase, signal }) {
  return getJson(apiBase, '/planner/config', signal);
}

/**
 * Update one or more config fields (send only what changed). Returns the full
 * updated config. Out-of-range values return 422.
 */
export function configPatchBody(patch) {
  // rev 11 §3.1 warns specifically about a full-object PATCH: a settings screen
  // that writes the whole config back would now carry `time_limit` and fail.
  // Ours sends only changed keys off a nine-key allow-list, so this cannot fire
  // today — it guards the next author of that screen.
  return stripEnginePolicy(patch);
}

export async function patchConfig({ apiBase, patch, signal }) {
  const resp = await authFetch(`${apiBase}/planner/config`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(configPatchBody(patch)),
    signal,
  });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** Restore built-in config defaults ("Reset to defaults"). Returns the config. */
export async function resetConfig({ apiBase, signal }) {
  const resp = await authFetch(`${apiBase}/planner/config/reset`, { method: 'POST', signal });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/**
 * The config fields the UI exposes, with controls/ranges (brief §4.2).
 * Split into everyday vs advanced so the Settings form can collapse the latter.
 */
export const CONFIG_FIELDS = [
  { key: 'over_tolerance_pct', control: 'number', min: 0, label: 'Max over-delivery %', group: 'basic' },
  { key: 'under_tolerance_pct', control: 'number', min: 0, label: 'Max under-delivery % (0 = never ship short)', group: 'basic' },
  { key: 'objective', control: 'select', options: ['min_overrun', 'min_sheets', 'max_utilization'], label: 'Objective', group: 'basic' },
  { key: 'return_top_k', control: 'number', min: 1, step: 1, label: 'How many plan options to return', group: 'basic' },
  { key: 'require_signoff', control: 'toggle', label: 'Plans need approval before press', group: 'basic' },
  { key: 'due_date_grace_days', control: 'number', min: 0, step: 1, label: 'Days late tolerated (scheduling)', group: 'basic' },
  { key: 'packing_efficiency_guess', control: 'slider', min: 0.01, max: 1, step: 0.01, label: 'Search centring (die packing)', group: 'advanced' },
  { key: 'search_radius', control: 'number', min: 0, step: 1, label: 'Search width (0 = seed only)', group: 'advanced' },
  { key: 'max_mne_calls', control: 'number', min: 1, step: 1, label: 'Search effort cap', group: 'advanced' },
];

/** Map an over/under tolerance source code to short display text (brief §3.1). */
export function toleranceSourceLabel(source) {
  switch (source) {
    case 'order_line': return 'from this order line';
    case 'request': return 'from request override';
    case 'config': return 'using shop default';
    case 'moq': return 'pinned by MOQ';
    default: return source || '';
  }
}

/* ------------------------------------------------------------------ */
/* Async planning — cancel + progress (Contract v1.2 addendum;          */
/* cancellation semantics per v1.3: engine-level interrupt, 202)        */
/* ------------------------------------------------------------------ */

/**
 * Job statuses (contract v2.0 §6.2): queued · running · succeeded · failed ·
 * cancelled · interrupted.
 *
 * `interrupted` is TERMINAL — the backend restarted (deploy or crash) while the
 * job was in flight. It is not a fault in the user's input.
 *
 * The terminal test is deliberately INVERTED: we enumerate the statuses that
 * mean "still working" and treat everything else — including statuses this
 * build has never heard of — as terminal. Per §6.2 ("treat any status you do
 * not recognise as terminal rather than polling forever") and §A.5 forward
 * compatibility, a future status must never strand the poll loop in an
 * infinite spin.
 */
export const PLAN_ACTIVE_STATUSES = ['queued', 'running'];
/** Known terminal statuses — for labels/copy. NOT the terminal test (see below). */
export const PLAN_TERMINAL_STATUSES = ['succeeded', 'failed', 'cancelled', 'interrupted'];
export function isPlanTerminal(status) {
  return !PLAN_ACTIVE_STATUSES.includes(status);
}

/**
 * Start a background planning job. Same request body as the sync requestPlan
 * (order_lines / line_ids / sheet / sku_defs / overrides — v1.1 §6). Input
 * validation (unknown SKU/line ids, MNE availability) happens synchronously and
 * throws 400/503 here, before any job exists — handle like the sync errors.
 *
 * @returns {Promise<{ job_id, status, poll, cancel }>}  — `poll`/`cancel` are
 *          ready-made relative URLs; use them directly (don't build paths).
 * @throws {PlannerApiError}
 */
export function startPlanAsync(args) {
  // Same body, different route. ⑬h: ONE builder for both routes, so the sync
  // and async paths cannot drift — which is exactly how `allowed_rotations`
  // survived six PRs on the export side.
  return postJson(args.apiBase, '/planner/plan/async', planRequestBody(args), args.signal);
}

/**
 * Poll a plan job's status/progress/result. The job object shape (v1.2 §3):
 *   { job_id, status, progress, result|null, error|null, elapsed_seconds, cancel_requested }
 *
 * `pollUrl` is the ready-made URL from startPlanAsync's response. A 404 means
 * the job is unknown or was reaped (~15 min after finishing) — the caller should
 * treat that as "result no longer available", not a hard error; PlannerApiError
 * carries status 404 so it can branch.
 *
 * @throws {PlannerApiError}
 */
export async function pollPlanJob({ apiBase, pollUrl, jobId, signal }) {
  const url = pollUrl ? `${apiBase}${pollUrl}` : `${apiBase}/planner/plan/jobs/${jobId}`;
  const resp = await authFetch(url, { signal });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/**
 * Request cancellation of a plan job — v1.3 semantics: the cancel token is
 * passed INTO the engine, which stops at its next safe point mid-computation;
 * terminal `cancelled` typically arrives < 1 s later via the poll (it used to
 * wait for the current nesting call to finish). Idempotent: cancelling twice,
 * or an already-finished job, is a harmless no-op.
 *
 * Responds **202 Accepted** (was 200 in v1.2) with a snapshot of the current
 * job state (same shape as pollPlanJob) — expect `status:"running"` +
 * `cancel_requested:true`. This is fire-and-forget-then-poll: the terminal
 * status arrives through the normal polling path, NOT in this response.
 * (We check `resp.ok`, so any 2xx passes.)
 *
 * A **404** means "nothing to cancel" — the job finished (and its token was
 * released), never existed, or runs on another instance. Per v1.3 §5 this is
 * the cancel-vs-completion race: NOT an error. Keep polling and branch on the
 * FINAL status — the job may well come back 'succeeded'.
 *
 * @throws {PlannerApiError}  (404 = already finished / unknown — see above)
 */
export async function cancelPlanJob({ apiBase, cancelUrl, jobId, signal }) {
  const url = cancelUrl ? `${apiBase}${cancelUrl}` : `${apiBase}/planner/plan/jobs/${jobId}/cancel`;
  const resp = await authFetch(url, { method: 'POST', signal });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}

/** List recent plan jobs (debug/recent view; not needed for the core flow). */
export function listPlanJobs({ apiBase, signal }) {
  return getJson(apiBase, '/planner/plan/jobs', signal);
}

/** Human label for a progress phase (v1.2 §4.1). */
export function phaseLabel(phase) {
  switch (phase) {
    case 'queued': return 'Queued…';
    case 'preparing': return 'Preparing…';
    case 'enumerating': return 'Finding layout options…';
    case 'evaluating': return 'Evaluating layouts…';
    case 'ranking': return 'Ranking plans…';
    case 'done': return 'Done';
    default: return phase || '';
  }
}

/**
 * Honest user-facing copy for a TERMINAL job status (contract v2.0 §6.2).
 *
 * `interrupted` gets the message the contract asks for — the server restarted,
 * it is not the user's fault, and the fix is to run it again. Anything we don't
 * recognise (a future status) is reported as a stopped job rather than silently
 * mislabelled as a failure, since we can't know which it was.
 *
 * @param {string} status
 * @param {string} [fallback] — server-supplied error text, if any.
 */
export function terminalStatusMessage(status, fallback) {
  switch (status) {
    case 'succeeded': return null;                     // nothing to say
    case 'cancelled': return 'Cancelled.';
    case 'interrupted':
      return 'The server restarted while this job was running — please run it again.';
    case 'failed':
      return fallback || 'The job failed.';
    default:
      return fallback || `This job stopped with an unrecognised status (“${status}”). Please run it again.`;
  }
}

/**
 * True when a polled job record is a DURABLE replay rather than live state
 * (contract v2.0 §6.3: `persisted:true`).
 *
 * On such a record `progress` is a terminal-shaped stub — counters zeroed and
 * `best_so_far` null — so it must NOT drive a live progress bar. Read `status`
 * and `result` instead.
 */
export function isPersistedRecord(job) {
  return !!(job && job.persisted === true);
}

/* ------------------------------------------------------------------ */
/* Response helpers                                                     */
/* ------------------------------------------------------------------ */

/**
 * Normalize a PlanCandidate into a shape the UI can render uniformly, modelling
 * it as potentially multi-sheet from the start (forward-compat for v1.4 where a
 * plan becomes a SET of forms). For v1.0 a candidate is a single form, so we
 * wrap it as a one-element `forms` array while preserving the flat fields.
 */
export function normalizeCandidate(candidate) {
  if (!candidate) return null;
  return {
    // flat v1.0 fields (kept as-is)
    up_counts: candidate.up_counts || {},
    sheets: candidate.sheets ?? 0,
    total_up: candidate.total_up ?? 0,
    utilization: candidate.utilization ?? 0,
    placements: candidate.placements || [],
    total_overrun: candidate.total_overrun ?? 0,
    max_overrun_pct: candidate.max_overrun_pct ?? 0,
    score: candidate.score,
    mne_job_id: candidate.mne_job_id ?? null,
    // PR ⑬a (prepress): surfaced explicitly rather than left to _raw, because
    // the utilization gate reads them on every render. cartons_placed is
    // pair-aware; prepress_tiled describes the OUTCOME, not the requested mode.
    cartons_placed: candidate.cartons_placed ?? null,
    prepress_applied: candidate.prepress_applied === true,
    prepress_tiled: candidate.prepress_tiled === true,
    // forward-compat: a list of forms. v1.0 = exactly one.
    forms: Array.isArray(candidate.forms) && candidate.forms.length
      ? candidate.forms
      : [{ up_counts: candidate.up_counts || {}, sheets: candidate.sheets ?? 0, utilization: candidate.utilization ?? 0 }],
    // keep the raw object so unknown fields survive (open-map rule)
    _raw: candidate,
  };
}

/** True if the candidate spans more than one form/sheet (v1.4+). */
export function isMultiForm(candidate) {
  return (normalizeCandidate(candidate)?.forms.length || 0) > 1;
}

/**
 * Map a plan candidate's up_counts to the per-file counts the vector flow uses.
 * Returns { [sku_id]: up_count }. The UI matches sku_id → uploaded file by name.
 */
export function upCountsFromCandidate(candidate) {
  return { ...(candidate?.up_counts || {}) };
}

/** Classify an overrun_pct into a badge level for display. */
export function overrunBadge(overrunPct) {
  const a = Math.abs(overrunPct || 0);
  if (a < 1) return 'ok';        // green — essentially on-target
  if (a < 5) return 'warn';      // amber — a few percent over/under
  return 'high';                 // red — large deviation
}

/* ------------------------------------------------------------------ */
/* SKU registration from the vector flow (Option A / trigger #2)       */
/* ------------------------------------------------------------------ */

/**
 * Map a `/export/vector` source object to a planner SKU. This is the bridge that
 * lets the Vector PDF die mode feed the SKU registry with NO backend change: the
 * vector response's `sources[].vertices` are already "CCW Y-up local-origin mm —
 * as sent to MNE", which is byte-for-byte the planner's `die_polygon` contract
 * (§3.1). So we reuse the detected die directly; no geometry is recomputed here.
 *
 * @param {Object} source  one entry of /export/vector `sources[]`
 * @returns {Object|null}  SKU object for upsertSkus(), or null if no usable polygon
 */
export function skuFromVectorSource(source) {
  if (!source || !source.source_id) return null;
  const poly = source.vertices;
  if (!Array.isArray(poly) || poly.length < 3) return null;   // need a real polygon
  return {
    sku_id: source.source_id,
    name: source.filename || source.source_id,
    die_polygon: poly,
  };
}

/**
 * Build the SKU array to register from a whole `/export/vector` result.
 * Skips any source without a usable polygon.
 */
export function skusFromVectorResult(result) {
  if (!result?.sources) return [];
  return result.sources.map(skuFromVectorSource).filter(Boolean);
}

/* ------------------------------------------------------------------ */
/* Die detection without nesting — POST /extract/detect-die            */
/* (Backend-provided; lets SKUs register on upload, no /export/vector  */
/*  run required. Frontend degrades gracefully if not yet deployed.)   */
/* ------------------------------------------------------------------ */

/**
 * Detect the die polygon(s) in uploaded PDF(s) WITHOUT running a nest.
 * Expected to mirror the detection stage of /export/vector and return, per file,
 * `{ source_id, filename, vertices, bbox_mm, is_rectangular, die_source }` —
 * the same geometry contract as /export/vector sources[].
 *
 * Multipart, file parts named `files` (consistent with /export/vector and
 * /export/preflight).
 *
 * @returns {Promise<{ results: Array }>}
 * @throws {PlannerApiError} — including 404 if the endpoint isn't deployed yet,
 *         which callers can catch to fall back to register-after-nest.
 */
export async function detectDie({ apiBase, files, signal }) {
  if (!Array.isArray(files) || files.length === 0) {
    throw new Error('detectDie: files must be a non-empty array');
  }
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  const resp = await authFetch(`${apiBase}/extract/detect-die`, { method: 'POST', body: form, signal });
  if (!resp.ok) throw await readError(resp);
  return resp.json();
}
